import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "../../../utils/config";
import { setUser } from "../../../redux/slices/authSlice";
import { notifyWarning } from "../../../helper/notify";
import { enableDemoData, disableDemoData } from "../../../utils/demoData";

// Customer side only (rendered by the customer layout). Draws nothing; it does two jobs:
//  0. the FREE plan shows DEMO data only: while the plan is Free, the Dashboard / Customers data requests are answered with
//     demo data (utils/demoData.js); the moment the plan is anything else the real requests go through again;
//  1. keeps `subscription` in Redux in step with the plan the customer really has (the sidebar chip, the demo-data mode and
//     the reminder below all read it) — loaded when the layout opens;
//  2. every 30 minutes, for the Free / Starter / Growth / Pro plans (not Agency), a short message: renew or upgrade for more
//     access permissions and profit. The time of the last message is kept in localStorage, so a reload does not restart the clock.
const EVERY_MS = 30 * 60 * 1000;
const KEY = "ms_plan_reminder_at";

const MESSAGES = {
  Free: [
    "You are on the Free plan — it shows demo data only. Upgrade to see your real stores and orders, unlock more modules and grow your profit.",
    "Free plan: demo data only. Choose Starter, Growth, Pro or Agency for real data, more access permissions and higher profit.",
  ],
  Starter: [
    "Renew or upgrade your Starter plan for more access permissions, more listings and higher profit.",
    "Your Starter plan: renew in time, or move up to Growth for auto-repricing, more marketplaces and more profit.",
  ],
  Growth: [
    "Renew your Growth plan to keep your access — or upgrade to Pro for more marketplaces, more AI credits and higher profit.",
    "Growth plan: renew on time, or upgrade to Pro for a lower supplier markup and more profit.",
  ],
  Pro: [
    "Renew your Pro plan to keep your access permissions — Agency adds client sub-accounts and the lowest supplier markup for more profit.",
    "Pro plan: renew in time, or upgrade to Agency for 25 users, client sub-accounts and a dedicated manager.",
  ],
};

export default function CustomerPlanSync() {
  const dispatch = useDispatch();
  const { token, side, subscription } = useSelector((s) => s.auth);
  const planRef = useRef(subscription);
  planRef.current = subscription;

  // 0) Free plan → demo data. A layout effect, so it is on BEFORE the page below asks for its data.
  const demo = side === "customer" && !!subscription?.isFree;
  useLayoutEffect(() => {
    if (demo) enableDemoData(); else disableDemoData();
    return () => disableDemoData();
  }, [demo]);

  // 1) the real plan, from the server
  useEffect(() => {
    if (!token || side !== "customer") return;
    let off = false;
    axios.get(`${API_BASE_URL}/api/subscriptions/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!off) dispatch(setUser({ subscription: data?.plan || null })); })
      .catch(() => {});
    return () => { off = true; };
  }, [token, side, dispatch]);

  // 2) the 30-minute reminder
  useEffect(() => {
    if (!token || side !== "customer") return undefined;
    let last = 0;
    try { last = Number(localStorage.getItem(KEY)) || 0; } catch { /* storage unavailable */ }
    if (!last) { last = Date.now(); try { localStorage.setItem(KEY, String(last)); } catch { /* storage unavailable */ } }
    const id = setInterval(() => {
      if (Date.now() - last < EVERY_MS) return;
      const plan = planRef.current;
      const list = plan && MESSAGES[plan.name];
      last = Date.now();
      try { localStorage.setItem(KEY, String(last)); } catch { /* storage unavailable */ }
      if (plan && plan.expired) { notifyWarning(`Your ${plan.name} plan has expired — renew it to keep your access.`, { autoClose: 12000 }); return; }
      if (list) notifyWarning(list[Math.floor(Math.random() * list.length)], { autoClose: 12000 });
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [token, side]);

  return null;
}
